import { useEffect, useMemo, useState } from 'react';
import {
  MagnifyingGlassIcon, ArrowPathIcon, ExclamationTriangleIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  useStorerooms, useCountedVans, useStoreroomStock, useVanStock, useAdjustStock,
} from '../hooks/useStockLevels';
import StockHistoryPanel from '../components/StockHistoryPanel';

// What is where: a storeroom's ledger balance, or a van's derived on-hand.
//
// Deliberately one screen with a toggle rather than two, because the question
// people actually ask is "where is this item", and switching sides to chase it
// should not mean navigating somewhere else.

const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 });

// Correcting one line. Shows what is on record, takes what it should be, and
// will not save without a reason — the reason is what makes the movement
// readable a month later.
function AdjustPanel({ row, mode, storeroomId, registration, onClose }) {
  // A combined cable line is several codes on the shelf, so the correction has
  // to name one of them: stock is counted, ordered and issued under a single
  // code, and an adjustment spread over the group would be a guess.
  const members = row.members || null;
  const [pick, setPick] = useState(() => (members ? String(members[0].code) : null));
  const chosen = members ? (members.find((m) => String(m.code) === pick) || members[0]) : null;
  const onRecord = chosen ? Number(chosen.qty) : Number(row.qty);
  const [qty, setQty] = useState(String(onRecord));
  const [reason, setReason] = useState('');
  const adjust = useAdjustStock();

  // Switching code re-points the form at that code's own figure.
  useEffect(() => { setQty(String(onRecord)); }, [onRecord]);

  const target = Number(qty);
  const valid = Number.isFinite(target) && target !== onRecord && reason.trim().length > 0
    && (!members || chosen?.id);
  const delta = Number.isFinite(target) ? target - onRecord : 0;

  const save = async () => {
    try {
      await adjust.mutateAsync({
        mode,
        storeroomId,
        registration,
        productId: members ? (mode === 'storeroom' ? chosen.id : null) : row.productId,
        stockItemId: members ? (mode === 'storeroom' ? null : chosen.id) : row.stockItemId,
        newQty: target,
        reason: reason.trim(),
      });
      onClose();
    } catch (err) {
      window.alert(err?.response?.data?.error || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-start justify-between gap-3">
          <h2 className="text-base font-bold text-gray-900">Adjust stock</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        <p className="mb-4 text-sm text-gray-600">
          <span className="font-mono text-xs text-gray-500">{chosen ? chosen.code : (row.sku || '—')}</span>{' '}
          {chosen ? chosen.name : row.name}
          <span className="block text-xs text-gray-400">
            {mode === 'storeroom' ? 'Storeroom ledger' : `Van ${registration}`}
          </span>
        </p>

        {members ? (
          <label className="mb-3 block">
            <span className="text-xs font-medium text-gray-600">Which code</span>
            <select
              id="adjust-code"
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            >
              {members.map((m) => (
                <option key={m.code} value={m.code} disabled={!m.id}>
                  {m.code}{m.name ? ` — ${m.name}` : ''} ({num(m.qty)} on record){m.id ? '' : ' — not in the catalogue'}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-gray-500">
              {row.name} is stocked under {members.length} codes. Correct the one that is wrong on the shelf.
            </span>
          </label>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">On record</span>
            <p className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm tabular-nums text-gray-700">
              {num(onRecord)}
            </p>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Should be</span>
            <input
              id="adjust-qty"
              type="number"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm tabular-nums focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </label>
        </div>

        {Number.isFinite(target) && delta !== 0 ? (
          <p className="mt-2 text-xs text-gray-500">
            This records an adjustment of{' '}
            <span className={`font-semibold ${delta < 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
              {delta > 0 ? '+' : ''}{num(delta)}
            </span>.
          </p>
        ) : null}

        <label className="mt-3 block">
          <span className="text-xs font-medium text-gray-600">Reason (required)</span>
          <input
            id="adjust-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. recount after goods-in error"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </label>

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={!valid || adjust.isPending}
            className="flex-1 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-40"
          >
            {adjust.isPending ? 'Saving…' : 'Save adjustment'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:border-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function StockLevels() {
  const [mode, setMode] = useState('storeroom');
  const [storeroomId, setStoreroomId] = useState('');
  const [van, setVan] = useState('');
  // 164 vans is too many to scroll, and nobody recalls a registration — so the
  // list is narrowed by typing, and an engineer's name finds his van.
  const [vanSearch, setVanSearch] = useState('');
  const [q, setQ] = useState('');
  const [inStock, setInStock] = useState(true);
  // Interchangeable cable codes folded together. On by default: a 70m run
  // stocked as Excel and PPC reads -8 and 42 apart, and 34 together — the
  // combined figure is the one that answers "have we got enough 70m".
  const [group, setGroup] = useState(true);
  const [adjusting, setAdjusting] = useState(null);
  const [history, setHistory] = useState(null);
  // Combined cable lines opened out to their own codes. The total answers "have
  // we got enough 70m"; this answers "under which code, and where is the
  // negative", which is the question you ask next.
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const toggleGroup = (key) => setOpenGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const { data: storerooms } = useStorerooms();
  const { data: vans } = useCountedVans();

  const room = useStoreroomStock(mode === 'storeroom' ? storeroomId : null, { q, inStock, group });
  const vanQ = useVanStock(mode === 'van' ? van : null, { q, inStock, group });

  const loading = mode === 'storeroom' ? room.isLoading : vanQ.isLoading;
  const fetching = mode === 'storeroom' ? room.isFetching : vanQ.isFetching;
  const refetch = mode === 'storeroom' ? room.refetch : vanQ.refetch;

  const rows = useMemo(() => {
    if (mode === 'storeroom') {
      return (room.data || []).map((r) => ({
        key: r.item_code, sku: r.item_code, name: r.name, category: r.category,
        qty: Number(r.qty_on_hand || 0), members: r.grouped ? r.members : null,
        // Only carried on a real line. A combined row inherits the first
        // member's id, which would adjust one code while looking like the group.
        productId: r.grouped ? null : (r.product_id ?? null),
      }));
    }
    return (vanQ.data?.items || []).map((r) => ({
      key: r.sku || r.stock_item_id, sku: r.sku, name: r.name, category: r.category,
      qty: Number(r.qty_on_hand || 0), members: r.grouped ? r.members : null,
      stockItemId: r.grouped ? null : (r.stock_item_id ?? null),
    }));
  }, [mode, room.data, vanQ.data]);

  const totals = useMemo(() => ({
    lines: rows.length,
    units: rows.reduce((t, r) => t + r.qty, 0),
    negative: rows.filter((r) => r.qty < 0).length,
  }), [rows]);

  // The chosen van always stays in the list, so narrowing the search cannot
  // blank the selection out from under you.
  const vanOptions = useMemo(() => {
    const needle = vanSearch.trim().toLowerCase();
    return (vans || []).filter((v) => !needle
      || v.registration === van
      || `${v.registration} ${v.driver_name || ''}`.toLowerCase().includes(needle));
  }, [vans, vanSearch, van]);

  const chosen = mode === 'storeroom' ? storeroomId : van;
  const chosenVan = (vans || []).find((v) => v.registration === van);
  const sel = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
  const uncounted = mode === 'van' && vanQ.data && vanQ.data.counted === false;

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Levels</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            What is on the shelf in a storeroom, or on a van. Storeroom figures come from the
            inventory ledger; van figures are worked out from the last count plus receipts,
            minus what job cards have used.
          </p>
        </div>
        <button type="button" onClick={() => refetch()} disabled={!chosen || fetching}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">
          <ArrowPathIcon className={`h-4 w-4 ${fetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-gray-300 p-0.5">
          {[['storeroom', 'Storeroom'], ['van', 'Van']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => { setMode(k); setQ(''); }}
              className={`rounded px-3 py-1.5 text-sm font-semibold ${mode === k ? 'bg-brand text-white' : 'text-gray-600 hover:text-brand'}`}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'storeroom' ? (
          <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)} className={sel}>
            <option value="">— choose a storeroom —</option>
            {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        ) : (
          <>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={vanSearch}
                onChange={(e) => setVanSearch(e.target.value)}
                placeholder="Engineer or registration…"
                className={`${sel} w-56 pl-8`}
              />
            </div>
            <select value={van} onChange={(e) => setVan(e.target.value)} className={`${sel} max-w-xs`}>
              <option value="">
                {vanOptions.length ? `— choose a van (${vanOptions.length}) —` : '— no van matches —'}
              </option>
              {vanOptions.map((v) => (
                <option key={v.registration} value={v.registration}>
                  {v.driver_name ? `${v.driver_name} — ${v.registration}` : v.registration}
                  {v.driver_source === 'last counted' ? ' (last counted by)' : ''}
                </option>
              ))}
            </select>
          </>
        )}

        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Item code or name…"
            className={`${sel} w-60 pl-8`} />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)}
            className="rounded border-gray-300 text-brand focus:ring-brand" />
          Hide zero lines
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={group} onChange={(e) => setGroup(e.target.checked)}
            className="rounded border-gray-300 text-brand focus:ring-brand" />
          Combine interchangeable cables
        </label>
      </div>

      {mode === 'van' && chosenVan ? (
        <p className="mt-3 text-sm text-gray-500">
          <span className="font-semibold text-gray-900">{chosenVan.registration}</span>
          {chosenVan.driver_name ? <> · {chosenVan.driver_name}</> : null}
          {' · last counted week '}{chosenVan.last_count_week}
          {chosenVan.last_count_date
            ? ` (${new Date(chosenVan.last_count_date).toLocaleDateString('en-GB')})` : ''}
        </p>
      ) : null}

      {chosen ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
            <div className="text-xl font-bold tabular-nums text-gray-900">{num(totals.lines)}</div>
            <div className="text-xs text-gray-500">lines</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
            <div className="text-xl font-bold tabular-nums text-gray-900">{num(totals.units)}</div>
            <div className="text-xs text-gray-500">units</div>
          </div>
          {/* Negative on hand means more has left than the last count said was
              there. It is a real state worth chasing, not a display bug. */}
          <div className={`rounded-lg border px-4 py-2 ${totals.negative > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}>
            <div className={`text-xl font-bold tabular-nums ${totals.negative > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
              {num(totals.negative)}
            </div>
            <div className="text-xs text-gray-500">negative lines</div>
          </div>
        </div>
      ) : null}

      {uncounted ? (
        <div className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {vanQ.data.message} It has to be counted before its stock can be shown.
        </div>
      ) : null}

      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Code</th>
              <th className="px-4 py-2.5">Item</th>
              <th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5 text-right">On hand</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {!chosen ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                Choose a {mode === 'storeroom' ? 'storeroom' : 'van'} to see what it holds.
              </td></tr>
            ) : loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            ) : rows.flatMap((r) => [(
              <tr key={r.key} className={r.qty < 0 ? 'bg-amber-50/60' : 'hover:bg-gray-50'}>
                <td className="px-4 py-2 font-mono text-xs text-gray-600">
                  {r.members ? <span className="text-gray-400">combined</span> : (r.sku || '—')}
                </td>
                <td className="px-4 py-2 text-gray-800">
                  {/* The constituent codes stay reachable: the point is to answer
                      "have we got enough 70m", not to hide which code it sits
                      under — the storeman still has to find it on a shelf. */}
                  {r.members ? (
                    <button type="button" onClick={() => toggleGroup(r.key)}
                      className="inline-flex items-center gap-1 text-left text-gray-800 hover:text-brand">
                      <ChevronRightIcon
                        className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${openGroups.has(r.key) ? 'rotate-90' : ''}`} />
                      {r.name || '—'}
                      <span className="ml-1 text-xs text-gray-400">
                        {r.members.filter((m) => !m.placeholder).length} codes
                      </span>
                    </button>
                  ) : (r.name || '—')}
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{r.category || '—'}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${r.qty < 0 ? 'font-semibold text-amber-700' : 'text-gray-900'}`}>
                  {num(r.qty)}
                  {r.qty < 0 ? <ExclamationTriangleIcon className="ml-1 inline h-4 w-4" /> : null}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right">
                  {/* History works on a combined storeroom row (one history across
                      its codes); a combined van row has no single item to follow. */}
                  {mode === 'storeroom' || !r.members ? (
                    <button
                      type="button"
                      onClick={() => setHistory(mode === 'storeroom'
                        ? {
                          mode, storeroomId, name: r.name,
                          codes: r.members ? r.members.map((m) => m.code) : [r.sku],
                        }
                        : {
                          mode, registration: van, stockItemId: r.stockItemId, code: r.sku, name: r.name,
                        })}
                      className="mr-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-brand hover:text-brand-dark"
                    >
                      History
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setAdjusting(r)}
                    className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-brand hover:text-brand-dark"
                    title={r.members ? 'Correct one of the codes in this combined line' : undefined}
                  >
                    Adjust
                  </button>
                </td>
              </tr>
            ),
            // One row per code inside an opened cable line. Placeholders — codes
            // this storeroom has no record of — stay out: they are offered in the
            // adjust picker, where correcting them is the point, but listing them
            // here would read as stock on a shelf that has never held any.
            ...(r.members && openGroups.has(r.key)
              ? r.members.filter((m) => !m.placeholder).map((m) => (
                <tr key={`${r.key}:${m.code}`} className="bg-gray-50/60 text-sm">
                  <td className="py-1.5 pl-8 pr-4 font-mono text-xs text-gray-500">{m.code}</td>
                  <td className="px-4 py-1.5 text-gray-600">
                    <span className="mr-1 text-gray-400">&#8627;</span>{m.name || r.name}
                  </td>
                  <td className="px-4 py-1.5" />
                  <td className={`px-4 py-1.5 text-right tabular-nums ${
                    Number(m.qty) < 0 ? 'font-semibold text-amber-700' : 'text-gray-700'}`}>
                    {num(m.qty)}
                    {Number(m.qty) < 0 ? <ExclamationTriangleIcon className="ml-1 inline h-4 w-4" /> : null}
                  </td>
                  <td className="whitespace-nowrap px-4 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => setHistory(mode === 'storeroom'
                        ? { mode, storeroomId, name: m.name || r.name, codes: [m.code] }
                        : { mode, registration: van, stockItemId: m.id, code: m.code, name: m.name || r.name })}
                      className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-brand hover:text-brand-dark"
                    >
                      History
                    </button>
                  </td>
                </tr>
              ))
              : []),
            ])}
            {chosen && !loading && !rows.length && !uncounted ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Nothing to show.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <StockHistoryPanel target={history} onClose={() => setHistory(null)} />

      {adjusting ? (
        <AdjustPanel
          row={adjusting}
          mode={mode}
          storeroomId={storeroomId}
          registration={van}
          onClose={() => setAdjusting(null)}
        />
      ) : null}
    </div>
  );
}

export default StockLevels;
